import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    TextField,
    Alert
} from '@mui/material';
import { NavigateNext, NavigateBefore } from '@mui/icons-material';
import { globalColor } from '../ConstantColors';
import secureLocalStorage from 'react-secure-storage';

const PerdiemReceiptDetails = ({ onNext, onBack }) => {
    const [formData, setFormData] = useState(() => {
        // Load initial data from localStorage or set defaults
        const storedData = secureLocalStorage.getItem('perdiemReceipt');
        return storedData
            ? JSON.parse(storedData)
            : {
                  received: 'No',
                  dateReceived: '',
                  amountReceived: '0.00'
              };
    });

    const [errors, setErrors] = useState({
        dateReceived: '',
        amountReceived: ''
    });

    // Save data to localStorage whenever formData changes
    useEffect(() => {
        secureLocalStorage.setItem('perdiemReceipt', JSON.stringify(formData));
    }, [formData]);

    const validateField = (name, value) => {
        if (formData.received === 'No') return '';

        switch (name) {
            case 'dateReceived':
                return !value ? 'Date is required when perdiem is received' : '';
            case 'amountReceived':
                return !value || parseFloat(value) <= 0 ? 'Amount is required and must be greater than 0' : '';
            default:
                return '';
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        if (name === 'received') {
            if (value === 'No') {
                setFormData({
                    received: value,
                    dateReceived: '',
                    amountReceived: '0.00'
                });
                setErrors({
                    dateReceived: '',
                    amountReceived: ''
                });
            } else {
                setFormData((prev) => ({
                    ...prev,
                    received: value
                }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
            setErrors((prev) => ({
                ...prev,
                [name]: validateField(name, value)
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {
            dateReceived: validateField('dateReceived', formData.dateReceived),
            amountReceived: validateField('amountReceived', formData.amountReceived)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some((error) => error !== '');
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.received === 'Yes' && !validateForm()) {
            return;
        }

        // Save to localStorage and proceed to the next step
        secureLocalStorage.setItem('perdiemReceipt', JSON.stringify(formData));
        onNext();
    };

    return (
        <Card elevation={3} sx={{ margin: 2 }}>
            <CardHeader
                title={
                    <Typography variant="h5" sx={{ color: globalColor, fontWeight: 'bold' }}>
                        PERDIEM RECEIPT DETAILS
                    </Typography>
                }
                subheader={
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Please provide details about your perdiem receipt
                    </Typography>
                }
            />
            <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                    <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                        <Typography variant="h6" sx={{ color: globalColor, mb: 2 }}>
                            Did you receive your perdiem for this acquittal?
                        </Typography>
                        <RadioGroup row name="received" value={formData.received} onChange={handleChange}>
                            <FormControlLabel value="Yes" control={<Radio sx={{ color: globalColor }} />} label="Yes" />
                            <FormControlLabel value="No" control={<Radio sx={{ color: globalColor }} />} label="No" />
                        </RadioGroup>
                    </FormControl>

                    {formData.received === 'Yes' && (
                        <Box sx={{ mb: 3 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Please fill in the date and amount received for your perdiem.
                            </Alert>
                            <TextField
                                fullWidth
                                type="date"
                                name="dateReceived"
                                label="Date Received"
                                value={formData.dateReceived}
                                onChange={handleChange}
                                error={Boolean(errors.dateReceived)}
                                helperText={errors.dateReceived}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 3 }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                name="amountReceived"
                                label="Amount Received"
                                value={formData.amountReceived}
                                onChange={handleChange}
                                error={Boolean(errors.amountReceived)}
                                helperText={errors.amountReceived}
                                inputProps={{ step: '0.01', min: '0' }}
                                sx={{ mb: 3 }}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button variant="outlined" onClick={onBack} startIcon={<NavigateBefore />} size="large">
                            Back
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            endIcon={<NavigateNext />}
                            size="large"
                            sx={{
                                backgroundColor: globalColor,
                                '&:hover': {
                                    backgroundColor: 'rgb(0, 100, 0)'
                                }
                            }}
                        >
                            Continue
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default PerdiemReceiptDetails;
